import { useEffect, useMemo, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { Sparkles } from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils/cn";
import { IssueIcon, thematicClasses } from "@/lib/thematic";
import { useAuth } from "@/lib/auth/auth-context";

import {
  useAvailableIssues,
  useDeleteIssue,
  useSelectIssue,
  type AvailableIssue,
} from "../api";

/**
 * Step 1 of the communication wizard: pick which self-diagnostic issues to
 * feature on the showcase. An issue is available when it has a Carried-Out
 * questionnaire action or a Done action-plan action (backend `issues/available/`
 * — parity with the legacy CommunicationIssuesSelectionView). Issues with a Done
 * *plan* action are forced-featured (checked, locked) and auto-selected on
 * arrival; the rest are opt-in. Grouped by thematic, colour-coded by pillar.
 */
/**
 * Issues already offered as featured, per user. Keyed by user so a shared
 * browser doesn't carry one member's decisions into another's session.
 */
const autoFeaturedKey = (userId: string | undefined) =>
  `activateur.communications.autoFeatured.${userId ?? "anon"}`;

function readAutoFeatured(userId: string | undefined): Set<string> {
  try {
    const raw = localStorage.getItem(autoFeaturedKey(userId));
    const ids: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(ids) ? ids.filter((id) => typeof id === "string") : []);
  } catch {
    // Storage unavailable (private mode, quota): fall back to offering them
    // again, which is the old behaviour rather than a broken step.
    return new Set();
  }
}

function writeAutoFeatured(userId: string | undefined, ids: Set<string>) {
  try {
    localStorage.setItem(autoFeaturedKey(userId), JSON.stringify([...ids]));
  } catch {
    // Best-effort: the worst case is re-offering an issue the member removed.
  }
}

export function IssuesStep() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const query = useAvailableIssues();
  const select = useSelectIssue();
  const remove = useDeleteIssue();

  // A Done plan action is a real achievement, so its issue is featured for you
  // the first time it appears. Only that first time: the ids are remembered, so
  // removing one is a decision that holds instead of being undone on the next
  // visit. Best-effort and per browser — the alternative is a server-side
  // "already offered" marker, which would mean changing the Django page too.
  const autoFeatured = useRef<Set<string>>(readAutoFeatured(user?.id));
  useEffect(() => {
    const issues = query.data ?? [];
    const unseen = issues.filter(
      (issue) => issue.has_plan_done && !autoFeatured.current.has(issue.issue_id),
    );
    if (unseen.length === 0) return;
    // Recorded whether or not we select it: an issue already featured has
    // plainly been offered before, and forgetting that would make the first
    // removal look like it failed — the effect would just put it straight back.
    for (const issue of unseen) autoFeatured.current.add(issue.issue_id);
    // Written before the requests go out, so a re-render mid-flight can't queue
    // them twice.
    writeAutoFeatured(user?.id, autoFeatured.current);
    for (const issue of unseen) {
      if (!issue.is_selected) select.mutate(issue.issue_id);
    }
  }, [query.data, select, user?.id]);

  const byThematic = useMemo(() => {
    const groups = new Map<
      string,
      { name: string; cssClass: string; issues: AvailableIssue[] }
    >();
    for (const issue of query.data ?? []) {
      // The old app renders an issue only when it has completed actions
      // ({% if choice.choice_label.completed_total > 0 %}) — there is nothing to
      // feature otherwise.
      if (issue.action_count === 0) continue;
      const group = groups.get(issue.thematic_id);
      if (group) group.issues.push(issue);
      else
        groups.set(issue.thematic_id, {
          name: issue.thematic_name,
          cssClass: issue.css_class,
          issues: [issue],
        });
    }
    return [...groups.values()];
  }, [query.data]);

  const toggle = (issue: AvailableIssue) => {
    // Plan-Done issues arrive pre-selected (see the effect above) but are not
    // locked: the old app renders an ordinary checkbox and deletes whatever you
    // untick on save. Unlike the old app, unticking one holds — it won't be
    // offered again.
    if (issue.is_selected && issue.choice_id) remove.mutate(issue.choice_id);
    else if (!issue.is_selected) select.mutate(issue.issue_id);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("communications.issues.title")}</CardTitle>
        <CardDescription>{t("communications.issues.description")}</CardDescription>
        {/* The Django step's « compléter plus d'enjeux »: the additional-issues
            picker, left out for a demonstration account, whose questionnaire
            is its training's. */}
        {!user?.is_demo_restricted && (
          <p className="text-sm text-muted-foreground">
            <Trans
              i18nKey="communications.issues.moreIssues"
              // Not `link`: Trans parses the string as HTML, where <link> is
              // a void element, so the anchor would render empty.
              components={{
                picker: (
                  <Link
                    to="/questionnaire"
                    search={{ view: "issues", from: "communications" }}
                    className="font-medium text-primary underline-offset-4 hover:underline"
                  />
                ),
              }}
            />
          </p>
        )}
      </CardHeader>
      <CardContent>
        {query.isPending ? (
          <p className="text-muted-foreground">{t("communications.loading")}</p>
        ) : query.isError ? (
          <p className="text-destructive">{t("communications.issues.loadError")}</p>
        ) : byThematic.length === 0 ? (
          <p className="text-muted-foreground">{t("communications.issues.empty")}</p>
        ) : (
          // `items-start`: a thematic is as tall as its own issues. Stretching
          // it to match its neighbours' row leaves the short one padded with
          // empty space below its last checkbox, which reads as a missing item.
          <div className="grid items-start gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {byThematic.map((group) => {
              const tc = thematicClasses(group.cssClass);
              return (
                <section
                  key={group.name}
                  className="overflow-hidden rounded-xl border"
                >
                  <h3
                    className={cn(
                      "px-3 py-2 text-sm font-bold",
                      tc.softBg,
                      tc.ink,
                    )}
                  >
                    {group.name}
                  </h3>
                  <ul className="divide-y divide-border">
                    {group.issues.map((issue) => {
                      const inputId = `issue-${issue.issue_id}`;
                      return (
                        <li key={issue.issue_id}>
                          <label
                            htmlFor={inputId}
                            className="flex cursor-pointer items-start gap-2.5 px-3 py-2.5 text-sm hover:bg-accent/50"
                          >
                            <input
                              id={inputId}
                              type="checkbox"
                              className="mt-0.5 size-4 accent-primary"
                              checked={issue.is_selected}
                              // Only the row being written is held, and only
                              // until its request answers: `choice_id` is the
                              // server's, so a second click before it lands
                              // would have nothing to delete. Disabling every
                              // checkbox instead made the whole list go dead on
                              // each click.
                              disabled={
                                (select.isPending &&
                                  select.variables === issue.issue_id) ||
                                (remove.isPending &&
                                  remove.variables === issue.choice_id)
                              }
                              onChange={() => toggle(issue)}
                            />
                            <span className="min-w-0 flex-1">
                              <span className="flex items-center gap-1.5 font-medium">
                                <IssueIcon
                                  icon={issue.icon}
                                  className="size-3.5 shrink-0"
                                  aria-hidden
                                />
                                {issue.issue_name}
                              </span>
                              <span className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                                <span>
                                  {t("communications.issues.actionCount", {
                                    count: issue.action_count,
                                  })}
                                </span>
                                <span>
                                  {t("communications.issues.courseOfActionCount", {
                                    count: issue.course_of_action_count,
                                  })}
                                </span>
                                {issue.has_plan_done && (
                                  <Sparkles
                                    className="size-3.5 shrink-0 text-theme-economic-ink"
                                    aria-label={t("communications.issues.planDoneHint")}
                                  >
                                    <title>
                                      {t("communications.issues.planDoneHint")}
                                    </title>
                                  </Sparkles>
                                )}
                              </span>
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
